import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import { useAuth } from '@/context/AuthContext';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@/hooks/useApiKeys';
import ApiKeyList from '@/components/apikeys/ApiKeyList';
import CreateApiKeyDialog from '@/components/apikeys/CreateApiKeyDialog';
import LoginModal from '@/components/common/LoginModal';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function ApiKeysPage() {
  usePageTitle('API Keys');

  const { user } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const { data: apiKeys, isLoading, error } = useApiKeys();
  const createApiKey = useCreateApiKey();
  const revokeApiKey = useRevokeApiKey();

  const handleCreateKey = async (name: string) => {
    const result = await createApiKey.mutateAsync(name);
    return result;
  };

  const handleRevokeKey = (keyId: string) => {
    revokeApiKey.mutate(keyId);
  };

  // If not logged in, show login prompt
  if (!user) {
    return (
      <Box>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <KeyIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" fontWeight={700} gutterBottom>
            MCP Server API Keys
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
            连接 Claude Desktop 或其他 MCP 客户端到 MoeMoe 市场情绪数据库。
            登录以创建和管理您的 API Keys。
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setShowLoginModal(true)}
          >
            登录
          </Button>
        </Box>

        {/* Features */}
        <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            MCP Server 功能:
          </Typography>
          <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
            <li>获取任意股票代码的实时情绪分析</li>
            <li>查看每日市场摘要，包含最看涨/看跌的股票</li>
            <li>追踪特定 YouTuber 的持仓和分析逻辑</li>
            <li>按关键词搜索所有频道的观点</li>
            <li>访问 60+ 中文财经 YouTuber 的数据</li>
          </Box>
        </Paper>

        <LoginModal open={showLoginModal} onClose={() => setShowLoginModal(false)} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <KeyIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" fontWeight={700} gutterBottom>
          API Keys
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          管理您的 MCP Server API Keys，用于 Claude Desktop 和其他客户端。
        </Typography>
      </Box>

      {/* Create Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateDialog(true)}
        >
          创建新 Key
        </Button>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          加载 API Keys 失败: {String(error)}
        </Alert>
      )}

      {/* API Key List */}
      {!isLoading && apiKeys && (
        <ApiKeyList
          apiKeys={apiKeys}
          onRevoke={handleRevokeKey}
          isRevoking={revokeApiKey.isPending}
        />
      )}

      {/* Rate Limits Info */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          速率限制
        </Typography>
        <Box component="ul" sx={{ pl: 2, color: 'text.secondary', m: 0 }}>
          <li>每分钟 100 次请求</li>
          <li>每天 1,000 次请求</li>
          <li>每月 10,000 次请求</li>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          需要更高的 rate limits？请 Discord 联系我们。
        </Typography>
      </Paper>

      {/* Usage Instructions */}
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          快速开始
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          1. 点击「创建新 Key」生成您的 API Key
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          2. 复制创建后显示的配置
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          3. 将配置粘贴到您的 MCP 客户端设置文件中，例如 Claude Desktop:
        </Typography>
        <Box
          component="code"
          sx={{
            display: 'block',
            p: 1.5,
            bgcolor: 'grey.900',
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            color: 'grey.300',
          }}
        >
          ~/Library/Application Support/Claude/claude_desktop_config.json
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          4. 重启客户端，开始询问股票情绪分析！
        </Typography>
      </Paper>

      {/* Create Dialog */}
      <CreateApiKeyDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreate={handleCreateKey}
        isCreating={createApiKey.isPending}
      />
    </Box>
  );
}
